
import Page from '@root/common/base/Page';
import ListSkeletonLoader from '@root/common/components/base/loader/ListSkeletonLoader.vue';
import { QueryResult, GetUniversalHeadlines } from '@root/modules/headlines/types/headlines';
import UniversalHeadlinesService from '@root/modules/headlines/services/UniversalHeadlines.service';
import HeadlineList from '@root/common/components/base/headline/HeadlineList.vue';
import SearchForm from '@root/modules/search/components/SearchForm.vue';
import isEqual from 'lodash/isEqual';
import getAliasByLocale from '@root/common/utils/getAliasByLocale';
import { getPageOffset } from '@root/common/utils/getPageOffset';
import { normalizeSearchObj } from '@root/modules/search/utils/normalizeSearchObj';
import { SearchVariables } from '@root/modules/search/types/SearchVariables';
import AdsLayout from '@root/common/components/layout/AdsLayout.vue';
import getPageAdsSettings from '@root/modules/search/utils/getPageAdsSettings';
import AuthorsService from '@root/modules/author/services/Authors.service';
import { Data as AuthorData } from '@root/modules/author/types/author';
import TagsService from '@root/modules/tags/services/Tags.service';
import { Data as TagData } from '@root/modules/tags/types/tag';
import LinkHandler from '@core/components/helpers/LinkHandler.vue';
import { headlineSettings } from '@root/modules/search/utils/config';
import isMobileView from '@root/common/utils/isMobileView';
import { searchCategories } from '@root/modules/search/utils/config';
import GetPageBlockService from '@root/modules/feed/services/pageBlock.service';
import { faCircleUser, faRectangleVerticalHistory } from '@delfi.lt/fa-kit/icons/modules/classic/regular';

interface EmptySearchResult {
  items: [];
  pager: Record<string, unknown>;
}

type SearchResults = QueryResult | EmptySearchResult;

type TagCloudTag = {
  key: string;
  value: string;
};

export default Page.extend({
  components: {
    HeadlineList,
    SearchForm,
    ListSkeletonLoader,
    AdsLayout,
    LinkHandler,
  },
  data() {
    return {
      variables: {} as SearchVariables,
      searchResult: {
        items: [],
        pager: {},
      } as SearchResults,
      headlineSettings,
      isMobileView: false,
      authors: [] as AuthorData[],
      tags: [] as TagData[],
      showCategorySelect: true,
      authorsSuggestCollapsed: true,
      tagsSuggestCollapsed: true,
      tagCloud: [] as TagCloudTag[],
      faCircleUser,
      faRectangleVerticalHistory,
    };
  },
  computed: {
    type() {
      const searchSettings = this.$channelConfig('page').search.component.searchForm;

      return searchSettings.type || 'BlockType101';
    },
    adPlacements() {
      const placements: Record<string, any> = this.$store.getters['adForm/getAdsPlacements'];

      return placements || {};
    },
    disabledAdsList() {
      return this.$store.getters['adForm/getDisabledAdsList'];
    },
    heading() {
      if (this.variables.search) {
        let heading = '';

        heading += this.$t('search.showing_results_for', { phrase: this.variables.search });

        if (this.searchResult.pager.count) {
          heading += ` <span>(${this.searchResult.pager.count})</span>`;
        }

        return heading;
      }

      return 'Naujausios';
    },
    authorsList() {
      return this.authorsSuggestCollapsed ? this.authors.slice(0, 5) : this.authors;
    },
    tagsList() {
      return this.tagsSuggestCollapsed ? this.tags.slice(0, 5) : this.tags;
    },
  },
  async fetch() {
    const { id } = this.$channelConfig('settings');
    const { search, from, to, page, order, type } = normalizeSearchObj(this.$route.query);
    const limit = 40;
    const offset = getPageOffset(limit, page);
    const { disallowedCategories = [] } = this.$channelConfig('page').search.settings;

    const excludeCategories = disallowedCategories.map((i: string) => Number(i));

    // Construct categoryId variable
    let categoryId: number[] = [];
    let categoryGroups: string[] | undefined = this.$route.query.categories;

    // Force array type for single selection
    if (categoryGroups && typeof categoryGroups === 'string') {
      categoryGroups = [categoryGroups];
    }

    if (categoryGroups?.length) {
      for (const categoryGroup of categoryGroups) {
        const group = searchCategories.find((c) => c.value === categoryGroup);
        if (group) {
          categoryId = [...categoryId, ...group.categories];
        }
      }
    }

    this.variables = { search, categoryId, categories: categoryGroups, from, to, order, type };

    // Set default value for type variable
    if (!type) {
      this.variables.type = 'article';
    }

    const searchValue = this.variables.search
      ? {
          field: ['TITLE', 'LEAD', 'BODY', 'SUMMARY', 'SUBTITLE'],
          value: this.variables.search,
        }
      : undefined;

    // Fetch tagCloud data, if search value is not present
    if (!searchValue) {
      const blockId = this.$channelConfig('page').search.component.tagCloud?.blockId;
      if (blockId) {
        const getPageBlockService = new GetPageBlockService({ req: this.$ssrContext?.req });
        const variables = { id: blockId };
        const response = await getPageBlockService.fetch(variables);
        if (response?.blocks?.length) {
          const block = response.blocks[0];
          this.tagCloud = block.attributes.find((a) => a.key === 'blockData' && a.value.config)?.value?.config;
        }
      }
    }

    const authorsSearch = this.variables.search ? { search: { field: ['name', 'name_rus'], value: this.variables.search }, offset: 0, limit: 50 } : undefined;
    if (authorsSearch) {
      const authorsService = new AuthorsService();
      const authorsResult = await authorsService.fetch(authorsSearch);
      this.authors = authorsResult ?? [];
    }

    const tagsSearch = this.variables.search ? { search: { field: 'name', value: this.variables.search }, offset: 0, limit: 50 } : undefined;
    if (tagsSearch) {
      const tagsService = new TagsService();
      const tagsResult = await tagsService.fetch(tagsSearch);
      this.tags = tagsResult?.tags?.items;
    }

    const headlinesSearch: GetUniversalHeadlines['variables'] = {
      search: searchValue,
      categoryId: this.variables.categoryId || undefined,
      excludeChannels: [],
      excludeCategories,
      offset,
      limit,
      orderBy: this.variables.order,
      getCount: true,
      getViews: id === 'tv',
      relatedMedia: {},
    };

    if (id !== 'ru') {
      headlinesSearch.excludeChannels?.push('www.delfi.lt/ru');
    } else {
      headlinesSearch.domain = 'www.delfi.lt/ru';
      this.showCategorySelect = false;
    }

    if (id !== 'en') {
      headlinesSearch.excludeChannels?.push('www.delfi.lt/en');
    } else {
      headlinesSearch.domain = 'www.delfi.lt/en';
      this.showCategorySelect = false;
    }

    if (id === 'tv') {
      headlinesSearch.domain = 'www.delfi.lt/tv';
      this.showCategorySelect = false;
    }

    if (this.variables.from && this.variables.to) {
      headlinesSearch.publishAt = {
        from: new Date(this.variables.from),
        to: new Date(this.variables.to),
      };
    }

    if (this.variables.type === 'video') {
      headlinesSearch.relatedMedia = { hasVideo: true };
    }

    if (this.variables.type === 'audio') {
      headlinesSearch.relatedMedia = { hasAudio: true };
    }

    try {
      const universalHeadlinesService = new UniversalHeadlinesService({ req: this.$ssrContext?.req });
      this.searchResult = await universalHeadlinesService.fetch(headlinesSearch);

      this.setPageAdsSettings(getPageAdsSettings({ $channelConfig: this.$channelConfig }));
    } catch (e) {
      this.handlePageError(e);
    }
  },
  watch: {
    '$route.query'() {
      this.$fetch();
    },
  },
  head() {
    const meta = this.getPageMeta();
    const title: string = this.$t('search.title');
    meta.setTitle({ title });

    return {
      title: meta.title,
      meta: meta.data,
      link: meta.link,
    };
  },
  created() {
    const { id } = this.$channelConfig('settings');
    if (id === 'tv') {
      this.headlineSettings.channel = false;
      this.headlineSettings.views = this.$channelConfig('settings').showViews ?? false;
      this.headlineSettings.videoDuration = true;
    }
  },
  mounted() {
    this.restoreScroll();
    this.isMobileView = isMobileView();
  },
  methods: {
    fetchSearchResults(variables: SearchVariables) {
      if (isEqual(this.$route.query, variables)) {
        return;
      }

      const fixedVariables: SearchVariables = Object.assign({}, variables);

      if (variables.date && variables.date.from) {
        fixedVariables.from = variables.date.from;
      }

      if (variables.date && variables.date.to) {
        fixedVariables.to = variables.date.to;
      }

      fixedVariables.categoryId = undefined;
      fixedVariables.categories = variables.categories;

      const { id, locale } = this.$channelConfig('settings');
      const channel = id !== 'delfi' ? `${id}/` : '';
      const path = getAliasByLocale(locale, 'search');

      this.$router.push({ path: `/${channel}${path}`, query: { ...fixedVariables } });
    },
    changePage() {
      this.$fetch();
    },
  },
});
